import React, { FC, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button, Form } from "@natera/form";
import {
  ClearErrors,
  ClearValidationError,
  GetValidationError,
  SetValidationError,
} from "@natera/platform/lib/hooks/useError";
import { useDialog, usePhoneValidation } from "@natera/platform/lib/hooks";
import { CancelProfileSetupDialog } from "@app/components";
import { getPhoneValidationErrorMessage, validateZipCode } from "@app/utils";
import { SetupProfileData } from "@app/provider/profile/types";
import { UserContext } from "@app/provider";
import { ProfileFormAccordion } from "@app/components/forms/profileFormAccordion";
import { useOnBackBrowserButton } from "@app/hooks";

import "./setupProfileForm.scss";

export const messages = defineMessages({
  setupProfileFormNext: {
    id: "setupProfileFormNext",
    defaultMessage: "Next",
  },
  setupProfileFormInvalidFirstName: {
    id: "setupProfileFormInvalidFirstName",
    defaultMessage: "Invalid First Name",
  },
  setupProfileFormInvalidLastName: {
    id: "setupProfileFormInvalidLastName",
    defaultMessage: "Invalid Last Name",
  },
  setupProfileFormInvalidDateOfBirth: {
    id: "setupProfileFormInvalidDateOfBirth",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
  setupProfileFormUnder18DateOfBirth: {
    id: "setupProfileFormUnder18DateOfBirth",
    defaultMessage:
      "The patient portal does not currently have test information for patients under 18 years old. Please reach out to your provider, or contact support@natera.com, for information on tests for patients under 18.",
  },
  setupProfileFormInvalidPhoneNumber: {
    id: "setupProfileFormInvalidPhoneNumber",
    defaultMessage: "Please enter a valid phone number.",
  },
  setupProfileFormUSPhoneNumber: {
    id: "setupProfileFormUSPhoneNumber",
    defaultMessage: "You must enter a US based number to create an account.",
  },
  setupProfileFormInvalidZipCode: {
    id: "setupProfileFormInvalidZipCode",
    defaultMessage: "Please enter a valid zip code.",
  },
  setupProfileFormZipTooltip: {
    id: "setupProfileFormZipTooltip",
    defaultMessage:
      "Required format: 5-digit number, or 9-digit number with a dash after the 5th digit",
  },
  setupProfileFormRequired: {
    id: "setupProfileFormRequired",
    defaultMessage: "This information is required.",
  },
});

interface SetupProfileFormProps {
  initialProfileData?: SetupProfileData;
  isLoading?: boolean;
  onSubmit: (profileData: SetupProfileData) => void;
  setValidationError: SetValidationError;
  getValidationError: GetValidationError;
  clearErrors: ClearErrors;
  clearValidationError: ClearValidationError;
}

const SetupProfileForm: FC<SetupProfileFormProps> = ({
  initialProfileData,
  isLoading,
  onSubmit,
  setValidationError,
  getValidationError,
  clearErrors,
  clearValidationError,
}) => {
  const intl = useIntl();
  const cancelProfileSetupDialog = useDialog(CancelProfileSetupDialog);

  useOnBackBrowserButton(() => {
    cancelProfileSetupDialog.open({
      onClose: cancelProfileSetupDialog.close,
    });
  });

  const { validatePhoneNumber: validateFromPS } = React.useContext(UserContext);
  const phoneValidation = usePhoneValidation();
  const MAX_NAME_LENGTH = 255;

  const [names, setNames] = useState({
    firstName: initialProfileData?.firstName ?? "",
    middleName: initialProfileData?.middleName ?? "",
    lastName: initialProfileData?.lastName ?? "",
    alternativeLastName: initialProfileData?.alternativeLastName ?? "",
  });

  const [profileFormData, setProfileFormData] = useState<FormData>();

  const [fieldValues, setFieldValues] = useState({
    dateOfBirth: initialProfileData?.dateOfBirth ?? "",
    mobileNumber: initialProfileData?.phone ?? "",
    zipCode: initialProfileData?.addresses?.[0]?.zipCode ?? "",
    street1: initialProfileData?.addresses?.[0]?.street1 ?? "",
    apartmentUnit: initialProfileData?.addresses?.[0]?.street2 ?? "",
    city: initialProfileData?.addresses?.[0]?.city ?? "",
    state: initialProfileData?.addresses?.[0]?.state ?? "",
  });

  const handleNamesChange: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setNames((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));

  const handleNamesUpdate: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setNames((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value.trim(),
    }));

  const handleFieldValueChange = (
    field: keyof typeof fieldValues,
    value: string
  ) => {
    setFieldValues((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const getProfileFormData = (formData: FormData) => {
    const firstName = formData.get("firstName")?.toString().trim();
    const middleName = formData.get("middleName")?.toString().trim();
    const lastName = formData.get("lastName")?.toString().trim();
    const alternativeLastName = formData
      .get("alternativeLastName")
      ?.toString()
      .trim();
    const dateOfBirth = formData.get("dateOfBirth")?.toString();
    const mobileNumber = formData.get("mobileNumber")?.toString();
    const zipCode = formData.get("zipCode")?.toString();
    const street1 = formData.get("street1")?.toString().trim();
    const apartmentUnit = formData.get("street2")?.toString().trim();
    const city = formData.get("city")?.toString().trim();
    const state = formData.get("state")?.toString().trim();

    return {
      firstName,
      middleName,
      lastName,
      alternativeLastName,
      dateOfBirth,
      mobileNumber,
      zipCode,
      street1,
      apartmentUnit,
      city,
      state,
    };
  };

  const validateProfileFormData = async (formData: FormData) => {
    const profileFormData = getProfileFormData(formData);
    const trimmedZipCode = (profileFormData.zipCode ?? "").trim();

    let hasErrors = false;

    const userForm = [
      { title: "firstName", value: profileFormData.firstName },
      { title: "lastName", value: profileFormData.lastName },

      { title: "dateOfBirth", value: profileFormData.dateOfBirth },

      { title: "street1", value: profileFormData.street1 },
      { title: "city", value: profileFormData.city },
      { title: "state", value: profileFormData.state },
      { title: "zipCode", value: trimmedZipCode },

      { title: "mobileNumber", value: profileFormData.mobileNumber },
    ];

    for (const field of userForm) {
      if (!field.value) {
        hasErrors = true;

        const requiredMessage = intl.formatMessage(
          messages.setupProfileFormRequired
        );

        if (field.title === "dateOfBirth") {
          // Don't show "required" error when we already have a specific day error
          if (!getValidationError("dateOfBirthDay")) {
            setValidationError("dateOfBirthRequired", requiredMessage);
          }
        } else {
          setValidationError(field.title, requiredMessage);
        }
      }
    }

    if (profileFormData.mobileNumber) {
      const phoneValidationErrorMessage = await getPhoneValidationErrorMessage({
        isMandatory: true,
        mobileNumber: profileFormData.mobileNumber,
        intl,
        phoneValidation,
        validateFromPS,
        pageName: "setupProfile",
      });

      if (phoneValidationErrorMessage) {
        setValidationError("mobileNumber", phoneValidationErrorMessage);
        hasErrors = true;
      }
    }

    // Check if DobInput has set a validation error (e.g., under 18, invalid date)
    if (
      getValidationError("dateOfBirthDay") ||
      getValidationError("dateOfBirthYear")
    ) {
      hasErrors = true;
    }

    if (trimmedZipCode && !validateZipCode(trimmedZipCode)) {
      setValidationError(
        "zipCode",
        intl.formatMessage(messages.setupProfileFormInvalidZipCode)
      );
      hasErrors = true;
    }

    if (hasErrors) {
      return undefined;
    }

    return {
      ...profileFormData,
      zipCode: trimmedZipCode,
    };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Save any DOB validation error from DobInput before clearing
    const existingDobYearError = getValidationError("dateOfBirthYear");
    const existingDobDayError = getValidationError("dateOfBirthDay");

    clearErrors();

    // Restore DOB errors if DobInput had set them (e.g., under 18, invalid day)
    if (existingDobYearError) {
      setValidationError("dateOfBirthYear", existingDobYearError);
    }
    if (existingDobDayError) {
      setValidationError("dateOfBirthDay", existingDobDayError);
    }

    const formData = new FormData(e.currentTarget);

    setProfileFormData(formData);

    const validatedProfileFormData = await validateProfileFormData(formData);

    if (validatedProfileFormData) {
      onSubmit({
        firstName: validatedProfileFormData.firstName,
        middleName: validatedProfileFormData.middleName,
        lastName: validatedProfileFormData.lastName,
        alternativeLastName: validatedProfileFormData.alternativeLastName,
        dateOfBirth: validatedProfileFormData.dateOfBirth,
        phone: validatedProfileFormData.mobileNumber,
        addresses: [
          {
            uid: "",
            countryCode: "US",
            city: validatedProfileFormData.city!,
            state: validatedProfileFormData.state!,
            street1: validatedProfileFormData.street1!,
            street2: validatedProfileFormData.apartmentUnit ?? null,
            zipCode: validatedProfileFormData.zipCode!,
          },
        ],
      });
    }
  };

  useEffect(() => {
    if (profileFormData) {
      validateProfileFormData(profileFormData);
    }
  }, [intl.locale]);

  return (
    <Form
      className="setup-profile-form__container"
      noValidate
      onSubmit={handleSubmit}
      data-testid="setupProfileForm"
    >
      {cancelProfileSetupDialog.getDialog()}
      <ProfileFormAccordion
        names={names}
        onNamesChange={handleNamesChange}
        onNamesUpdate={handleNamesUpdate}
        dateOfBirth={fieldValues.dateOfBirth}
        onDateOfBirthChange={(date) =>
          handleFieldValueChange("dateOfBirth", date)
        }
        street1={fieldValues.street1}
        onStreet1Change={(street1) =>
          handleFieldValueChange("street1", street1)
        }
        apartmentUnit={fieldValues.apartmentUnit}
        onApartmentUnitChange={(apartmentUnit) =>
          handleFieldValueChange("apartmentUnit", apartmentUnit)
        }
        city={fieldValues.city}
        onCityChange={(city) => handleFieldValueChange("city", city)}
        state={fieldValues.state}
        onStateChange={(state) => handleFieldValueChange("state", state)}
        zipCode={fieldValues.zipCode}
        onZipCodeChange={(zipCode) =>
          handleFieldValueChange("zipCode", zipCode)
        }
        mobileNumber={fieldValues.mobileNumber}
        onMobileNumberChange={(phone) =>
          handleFieldValueChange("mobileNumber", phone)
        }
        currentPhone={initialProfileData?.phone}
        getValidationError={getValidationError}
        setValidationError={setValidationError}
        clearValidationError={clearValidationError}
        sections={{
          showAlternativeLastName: true,
          showDateOfBirth: true,
          showAddress: true,
          showMobileNumber: true,
        }}
        initialDateOfBirth={initialProfileData?.dateOfBirth}
        maxNameLength={MAX_NAME_LENGTH}
        withPadding={true}
        showNameHints
      />
      <Button type="submit" unelevated loading={isLoading}>
        {!isLoading && intl.formatMessage(messages.setupProfileFormNext)}
      </Button>
    </Form>
  );
};

export default SetupProfileForm;
